import { supabase } from '../config/supabase';

/**
 * Calculate popularity score for a product based on sales and reviews
 * @param {Object} product - Product object
 * @param {Object} salesData - Map of product_id to sales count
 * @param {Object} reviewsData - Map of product_id to { count, averageRating }
 * @returns {number} Popularity score (higher is better)
 */
export const calculatePopularityScore = (product, salesData = {}, reviewsData = {}) => {
  const salesCount = salesData[product.id] || 0;
  const reviewData = reviewsData[product.id] || { count: 0, averageRating: 0 };
  const reviewCount = reviewData.count || 0;
  const averageRating = reviewData.averageRating || 0;

  // Calculate popularity score:
  // - Sales count: 1 point per sale
  // - Reviews: 2 points per review
  // - Rating: 5 points per star (max 25 points for 5 stars)
  // This gives more weight to reviews and ratings
  const salesScore = salesCount * 1;
  const reviewScore = reviewCount * 2;
  const ratingScore = averageRating * 5;

  return salesScore + reviewScore + ratingScore;
};

/**
 * Fetch sales data for products
 * @param {Array} productIds - Array of product IDs
 * @returns {Promise<Object>} Map of product_id to sales count
 */
export const fetchSalesData = async (productIds) => {
  if (!productIds || productIds.length === 0) return {};

  try {
    const { data, error } = await supabase
      .from('order_items')
      .select('product_id, quantity, orders!inner(payment_status)')
      .in('product_id', productIds)
      .neq('orders.payment_status', 'unpaid');

    if (error) {
      console.error('Error fetching sales data:', error);
      return {};
    }

    // Count total quantity sold per product
    const salesMap = {};
    (data || []).forEach(item => {
      const productId = item.product_id;
      const quantity = item.quantity || 1;
      salesMap[productId] = (salesMap[productId] || 0) + quantity;
    });

    return salesMap;
  } catch (error) {
    console.error('Error in fetchSalesData:', error);
    return {};
  }
};

/** Chunked fetch for large product lists (avoids huge .in() queries). */
export const fetchSalesDataBatched = async (productIds, chunkSize = 150) => {
  const ids = [...new Set((productIds || []).map((id) => String(id)).filter(Boolean))];
  if (ids.length === 0) return {};
  const merged = {};
  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    // eslint-disable-next-line no-await-in-loop
    const part = await fetchSalesData(chunk);
    Object.assign(merged, part);
  }
  return merged;
};

/**
 * Production-safe sales fetch:
 * 1) DB RPC (security definer) to bypass anon RLS constraints safely
 * 2) Edge function fallback
 * 3) Direct query fallback (local/dev)
 * @param {Array<string|number>} productIds
 * @returns {Promise<Object>} Map of product_id to units sold
 */
export const fetchSalesDataPublicSafe = async (productIds) => {
  const ids = [...new Set((productIds || []).map((id) => String(id)).filter(Boolean))];
  if (ids.length === 0) return {};

  try {
    const { data: rpcRows, error: rpcError } = await supabase.rpc('get_public_product_sales_stats', {
      p_product_ids: ids
    });

    if (!rpcError && Array.isArray(rpcRows)) {
      return rpcRows.reduce((acc, row) => {
        const key = String(row?.product_id || '');
        if (!key) return acc;
        const units = Number(row?.units_sold || 0);
        acc[key] = Number.isFinite(units) ? units : 0;
        return acc;
      }, {});
    }
  } catch (rpcErr) {
    console.warn('RPC sales fetch failed, trying edge function:', rpcErr);
  }

  try {
    const { data, error } = await supabase.functions.invoke('get-product-sales-stats', {
      body: { productIds: ids }
    });

    if (!error && data && typeof data.salesData === 'object' && data.salesData !== null) {
      return data.salesData;
    }
  } catch (edgeErr) {
    console.warn('Edge sales fetch failed, trying direct query fallback:', edgeErr);
  }

  return fetchSalesDataBatched(ids);
};

/**
 * Units sold + line revenue per product from paid orders only (excludes unpaid).
 * @param {Array<string|number>} productIds
 * @returns {Promise<Object>} Map of product id (string) to { units: number, revenue: number }
 */
export const fetchProductSalesStats = async (productIds) => {
  if (!productIds || productIds.length === 0) return {};

  try {
    const { data, error } = await supabase
      .from('order_items')
      .select('product_id, quantity, line_total, orders!inner(payment_status)')
      .in('product_id', productIds)
      .neq('orders.payment_status', 'unpaid');

    if (error) {
      console.error('Error fetching product sales stats:', error);
      return {};
    }

    const map = {};
    (data || []).forEach((row) => {
      const id = row.product_id;
      if (id == null) return;
      const key = String(id);
      const qty = parseInt(row.quantity, 10) || 0;
      const line = parseFloat(row.line_total) || 0;
      if (!map[key]) map[key] = { units: 0, revenue: 0 };
      map[key].units += qty;
      map[key].revenue += line;
    });
    return map;
  } catch (err) {
    console.error('Error in fetchProductSalesStats:', err);
    return {};
  }
};

/** Chunked fetch for large product lists (avoids huge .in() queries). */
export const fetchProductSalesStatsBatched = async (productIds, chunkSize = 150) => {
  const ids = [...new Set((productIds || []).map((id) => String(id)).filter(Boolean))];
  if (ids.length === 0) return {};
  const merged = {};
  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    const part = await fetchProductSalesStats(chunk);
    Object.assign(merged, part);
  }
  return merged;
};

/**
 * Fetch reviews data for products
 * @param {Array} productIds - Array of product IDs
 * @returns {Promise<Object>} Map of product_id to { count, averageRating }
 */
export const fetchReviewsData = async (productIds) => {
  if (!productIds || productIds.length === 0) return {};

  try {
    const { data, error } = await supabase
      .from('product_ratings')
      .select('product_id, stars, status')
      .in('product_id', productIds)
      .eq('status', 'approved'); // Only count approved reviews

    if (error) {
      console.error('Error fetching reviews data:', error);
      return {};
    }

    // Calculate review count and average rating per product
    const reviewsMap = {};
    (data || []).forEach(rating => {
      const productId = rating.product_id;
      if (!reviewsMap[productId]) {
        reviewsMap[productId] = { count: 0, totalStars: 0 };
      }
      reviewsMap[productId].count += 1;
      reviewsMap[productId].totalStars += rating.stars || 0;
    });

    // Calculate average ratings
    const result = {};
    Object.keys(reviewsMap).forEach(productId => {
      const data = reviewsMap[productId];
      result[productId] = {
        count: data.count,
        averageRating: data.count > 0 ? data.totalStars / data.count : 0
      };
    });

    return result;
  } catch (error) {
    console.error('Error in fetchReviewsData:', error);
    return {};
  }
};

/** Chunked fetch for large product lists (avoids huge .in() queries). */
export const fetchReviewsDataBatched = async (productIds, chunkSize = 150) => {
  const ids = [...new Set((productIds || []).map((id) => String(id)).filter(Boolean))];
  if (ids.length === 0) return {};
  const merged = {};
  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    // eslint-disable-next-line no-await-in-loop
    const part = await fetchReviewsData(chunk);
    Object.assign(merged, part);
  }
  return merged;
};

/**
 * Sort products: boosted first, then by popularity (sales + reviews)
 * @param {Array} products - Array of products to sort
 * @param {Object} salesData - Map of product_id to sales count
 * @param {Object} reviewsData - Map of product_id to { count, averageRating }
 * @returns {Array} Sorted products
 */
export const sortProductsByBoostAndPopularity = (products, salesData = {}, reviewsData = {}) => {
  return [...products].sort((a, b) => {
    // First: Boosted products come first
    if (a.boost && !b.boost) return -1;
    if (!a.boost && b.boost) return 1;

    // If both are boosted or both are not boosted, sort by popularity
    const scoreA = calculatePopularityScore(a, salesData, reviewsData);
    const scoreB = calculatePopularityScore(b, salesData, reviewsData);

    // Higher score comes first
    if (scoreB !== scoreA) return scoreB - scoreA;

    // Deterministic fallback: newest products first when popularity ties
    const dateA = new Date(a?.created_at || 0).getTime();
    const dateB = new Date(b?.created_at || 0).getTime();
    if (dateB !== dateA) return dateB - dateA;

    // Final deterministic tie-break by id
    return String(b?.id || '').localeCompare(String(a?.id || ''));
  });
};



